import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Badge variant styles using Class Variance Authority (CVA)
 *
 * Defines visual variants and size options for the Badge component.
 * All styles use design tokens from the HEN Design System.
 *
 * These styles override the shadcn badge base styles. The `!important` flags
 * are necessary to override shadcn's default styles (rounded-full, text-xs, etc.)
 * with HEN Design System tokens.
 *
 * ## Variants
 * - **primary** (default): Dark purple background (bg-primary-900) with white text - Most prominent, use for primary emphasis
 * - **secondary**: Light gray background (bg-neutral-100) with secondary text (text-secondary-700) - Use for secondary emphasis or less prominent counts
 * - **light**: Light gray background (bg-neutral-100) with purple text (text-primary-700) - Subtle emphasis, use for secondary labels
 * - **dark**: Dark neutral background (bg-neutral-900) with white text - High contrast option on light backgrounds where primary color is not appropriate
 * - **accent**: Accent background (bg-accent-200) with inherited text color - Use sparingly to draw attention to special states or highlighted metadata
 * - **outline**: White background with border (border-neutral-200) - Use on colored backgrounds for visibility
 * - **neutral**: Minimal gray background (bg-neutral-50) - Least prominent, for tags/metadata
 *
 * ## Sizes
 * - **sm** (default): Compact for inline use - px-xxs, py-[3px], text-l2, leading-l2
 * - **lg**: Larger for standalone badges - px-xs, py-1, text-l1, leading-l1
 *
 * ## Base Styles (Override shadcn defaults)
 * - Layout: flex with max-w-fit and gap-1 for icon spacing
 * - Border radius: rounded-xl (overrides shadcn's rounded-full)
 * - Typography: font-body font-bold (overrides shadcn's font-semibold)
 * - Focus: Custom shadow for HEN design system (overrides shadcn's ring)
 * - Dark mode: Disabled (HEN doesn't use dark mode)
 *
 * ## Design Token Overrides
 * - Spacing: Uses px-xxs/px-xs, py-[3px]/py-1 (overrides shadcn's px-2.5 py-0.5)
 * - Typography: Uses text-l1/text-l2 (overrides shadcn's text-xs)
 * - Border: Variant-specific borders (overrides shadcn's default border)
 */
export const badgeVariants = cva(
  [
    'flex max-w-fit gap-1 py-1 !ring-0 !ring-offset-0 rounded-xl not-italic font-body font-bold focus:!outline-none focus:!shadow-focus-shadow',
    'dark:bg-unset dark:text-unset dark:border-unset text-unset border-unset border-unset',
  ],
  {
    variants: {
      variant: {
        primary: 'text-white border-0 border-none bg-primary-900',
        secondary: 'text-secondary-700 border-0 border-none bg-neutral-100',
        light: 'text-primary-700 border-0 border-none bg-neutral-100',
        dark: 'text-white border-0 border-none bg-neutral-900',
        accent: 'text-black border-0 border-none bg-accent-200',
        outline: 'bg-white border border-neutral-200',
        neutral: 'bg-neutral-50 border-none border-0',
      },
      size: {
        sm: 'px-xxs !text-l2 leading-l2',
        lg: 'px-xs !text-l1 leading-l1',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'sm',
    },
  },
);

/**
 * Type for Badge variant props
 */
export type BadgeVariantProps = VariantProps<typeof badgeVariants>;
